import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type { PlaybackPhase, PlaybackState, PlayedItem, QueueItem, ReadyStatus, TheaterSettings } from '../lib/api';
import type { CharacterId, ReactionId, SeatId, ThrowableId } from '../theater/catalog';
import type { ActiveThrow } from '../components/theater/TheaterEffects';
import type {
  RareAnnouncement,
  TheaterChatMsg,
  TheaterOccupantView,
  TheaterReactionView,
} from '../components/theater/VirtualTheater';
import { seatPercent, THROWABLES } from '../theater/catalog';

export function useSocket(enabled: boolean, roomCode: string | null = null) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [playback, setPlayback] = useState<PlaybackState | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [history, setHistory] = useState<PlayedItem[]>([]);
  const [correction, setCorrection] = useState<{
    position_seconds: number;
    is_playing: boolean;
    phase?: PlaybackPhase;
  } | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [clockOffset, setClockOffset] = useState(0);

  const [occupants, setOccupants] = useState<TheaterOccupantView[]>([]);
  const [theaterCount, setTheaterCount] = useState(0);
  const [inTheater, setInTheater] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<TheaterChatMsg[]>([]);
  const [reactions, setReactions] = useState<TheaterReactionView[]>([]);
  const [throws, setThrows] = useState<ActiveThrow[]>([]);
  const [rareAnnouncement, setRareAnnouncement] = useState<RareAnnouncement | null>(null);
  const [theaterNotice, setTheaterNotice] = useState<string | null>(null);
  const [theaterSettings, setTheaterSettings] = useState<TheaterSettings | null>(null);
  const [throwables, setThrowables] = useState<
    Array<{ id: ThrowableId; name: string; emoji: string; cooldownMs?: number }>
  >([]);
  const myUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const socket = io({
      path: `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/socket.io`,
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: roomCode ? { roomCode } : {},
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setSyncError(null);
      const t0 = Date.now();
      socket.emit('playback:ping', { t0 }, (res: { t0: number; t1: number }) => {
        const t2 = Date.now();
        if (!res?.t1) return;
        setClockOffset(res.t1 - (t0 + t2) / 2);
      });
    });
    socket.on('disconnect', () => {
      setConnected(false);
      setInTheater(false);
      setSyncError('Playback sync was lost. Reconnecting.');
    });
    socket.on('connect_error', () => {
      setSyncError('Playback sync was lost. Reconnecting.');
    });
    socket.on('playback:state', (state: PlaybackState) => {
      setPlayback(state);
    });
    socket.on(
      'playback:tick',
      (tick: {
        position_seconds: number;
        is_playing: boolean;
        movie_id: string;
        server_time: string;
        server_time_ms?: number;
        phase?: PlaybackPhase;
        session_id?: string | null;
        play_at_ms?: number | null;
      }) => {
        setPlayback((prev) => {
          if (!prev?.movie || prev.movie.id !== tick.movie_id) return prev;
          return {
            ...prev,
            position_seconds: tick.position_seconds,
            is_playing: tick.is_playing,
            server_time: tick.server_time,
            server_time_ms: tick.server_time_ms ?? prev.server_time_ms,
            phase: tick.phase ?? prev.phase,
            session_id: tick.session_id ?? prev.session_id,
            play_at_ms: tick.play_at_ms ?? prev.play_at_ms,
          };
        });
      },
    );
    socket.on(
      'playback:correct',
      (payload: { position_seconds: number; is_playing: boolean }) => setCorrection(payload),
    );
    socket.on('queue:state', (payload: { items: QueueItem[] }) => setQueue(payload.items));
    socket.on('history:state', (payload: { items: PlayedItem[] }) => setHistory(payload.items || []));
    socket.on('queue:updated', () => {
      window.dispatchEvent(new Event('queue:updated'));
    });
    socket.on('viewers:count', (payload: { count: number }) => setViewerCount(payload.count));
    socket.on('playback:error', (payload: { error: string }) => setSyncError(payload.error));

    socket.on(
      'theater:presence',
      (payload: { count: number; occupants: TheaterOccupantView[]; settings?: TheaterSettings }) => {
        setTheaterCount(payload.count);
        setOccupants(payload.occupants || []);
        if (payload.settings) setTheaterSettings(payload.settings);
        if (myUserIdRef.current) {
          setInTheater(payload.occupants.some((o) => o.userId === myUserIdRef.current));
        }
      },
    );
    socket.on('theater:settings', (settings: TheaterSettings) => setTheaterSettings(settings));
    socket.on(
      'theater:throwables',
      (payload: {
        items: Array<{ id: ThrowableId; name: string; emoji: string; cooldownMs?: number }>;
      }) => {
        setThrowables(payload.items || []);
      },
    );
    socket.on('theater:chat-history', (payload: { messages: TheaterChatMsg[] }) => {
      setChatMessages(payload.messages || []);
    });
    socket.on(
      'theater:join',
      (payload: {
        userId: string;
        username: string;
        displayName: string | null;
        character: CharacterId;
        seat: SeatId;
        seatKey: string;
        timestamp: number;
      }) => {
        setTheaterNotice(`${payload.username.toUpperCase()} ENTERED THE THEATER`);
        window.setTimeout(() => setTheaterNotice(null), 3000);
      },
    );
    socket.on(
      'theater:leave',
      (payload: { userId: string; username: string; reason?: string; graceMs?: number }) => {
        if (payload.reason === 'disconnect' && payload.graceMs) {
          // Grace period: the seat may return. Show a leave notice only when they are fully gone.
          return;
        }
        setTheaterNotice(`${payload.username.toUpperCase()} LEFT THE THEATER`);
        window.setTimeout(() => setTheaterNotice(null), 3000);
        if (payload.userId === myUserIdRef.current) setInTheater(false);
      },
    );
    socket.on('theater:chat', (event: TheaterChatMsg) => {
      setChatMessages((prev) => [...prev.slice(-49), event]);
    });
    socket.on('theater:reaction', (event: TheaterReactionView) => {
      setReactions((prev) => [...prev.slice(-20), event]);
    });
    socket.on(
      'theater:throw',
      (event: {
        id: string;
        username: string;
        objectType: ThrowableId;
        originSeat: SeatId;
        targetX: number;
        targetY: number;
        flight: string;
        impact: string;
        durationMs: number;
        rareEvent?: string | null;
        timestamp: number;
      }) => {
        const def = THROWABLES[event.objectType];
        const seatPos = seatPercent(event.originSeat);
        // Origin from character upper body (slightly above seat anchor)
        const originX = seatPos.x;
        const originY = Math.max(8, seatPos.y - 10);
        const active: ActiveThrow = {
          id: event.id,
          objectType: event.objectType,
          originX,
          originY,
          targetX: event.targetX,
          targetY: event.targetY,
          flight: event.flight || def?.flight || 'arc-fast',
          impact: event.impact || def?.impact || 'splat',
          durationMs: event.durationMs || def?.durationMs || 3000,
          rareEvent: event.rareEvent,
          username: event.username,
          timestamp: event.timestamp,
        };
        setThrows((prev) => [...prev.slice(-14), active]);
      },
    );
    socket.on(
      'theater:rare-event',
      (payload: {
        id: string;
        announcement: string;
        durationMs: number;
        timestamp: number;
      }) => {
        setRareAnnouncement({
          id: payload.id,
          announcement: payload.announcement,
          durationMs: payload.durationMs,
          timestamp: payload.timestamp,
        });
        window.setTimeout(() => setRareAnnouncement(null), payload.durationMs || 3500);
      },
    );
    socket.on('theater:effects-cleared', () => setThrows([]));

    const pingTimer = window.setInterval(() => {
      const t0 = Date.now();
      socket.emit('playback:ping', { t0 }, (res: { t0: number; t1: number }) => {
        const t2 = Date.now();
        if (!res?.t1) return;
        const sample = res.t1 - (t0 + t2) / 2;
        setClockOffset((prev) => (prev === 0 ? sample : prev * 0.6 + sample * 0.4));
      });
    }, 15_000);

    return () => {
      window.clearInterval(pingTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, roomCode]);

  const sendHeartbeat = (
    position: number,
    extra?: { ended?: boolean; duration?: number },
  ) => {
    socketRef.current?.emit('playback:heartbeat', extra ? { position, ...extra } : { position });
  };

  const sendReadyStatus = useCallback(
    (sessionId: string, status: ReadyStatus, errorCode?: number) => {
      socketRef.current?.emit('playback:ready', {
        session_id: sessionId,
        status,
        error_code: errorCode,
      });
    },
    [],
  );

  const setMyUserId = useCallback((id: string) => {
    myUserIdRef.current = id;
  }, []);

  const joinTheater = useCallback(() => {
    setJoinError(null);
    socketRef.current?.emit('theater:join', {}, (res: { ok?: boolean; error?: string }) => {
      if (!res?.ok) {
        setJoinError(res?.error || 'Could not enter theater.');
        setInTheater(false);
      } else {
        setInTheater(true);
      }
    });
  }, []);

  const leaveTheater = useCallback(() => {
    socketRef.current?.emit('theater:leave', {});
    setInTheater(false);
  }, []);

  const sendChat = useCallback((message: string) => {
    socketRef.current?.emit('theater:chat', { message }, (res: { ok?: boolean; error?: string }) => {
      if (!res?.ok && res?.error) setJoinError(res.error);
    });
  }, []);

  const sendThrow = useCallback((objectType: ThrowableId, targetX: number, targetY: number) => {
    socketRef.current?.emit(
      'theater:throw',
      { objectType, targetX, targetY },
      (res: { ok?: boolean; error?: string }) => {
        if (!res?.ok && res?.error) setJoinError(res.error);
      },
    );
  }, []);

  const sendReaction = useCallback((reaction: ReactionId) => {
    socketRef.current?.emit(
      'theater:reaction',
      { reaction },
      (res: { ok?: boolean; error?: string }) => {
        if (!res?.ok && res?.error) setJoinError(res.error);
      },
    );
  }, []);

  return {
    connected,
    viewerCount,
    playback,
    setPlayback,
    queue,
    setQueue,
    history,
    setHistory,
    correction,
    clearCorrection: () => setCorrection(null),
    syncError,
    clearSyncError: () => setSyncError(null),
    sendHeartbeat,
    sendReadyStatus,
    clockOffset,
    socket: socketRef,
    // theater
    occupants,
    theaterCount,
    inTheater,
    joinError,
    clearJoinError: () => setJoinError(null),
    chatMessages,
    reactions,
    throws,
    rareAnnouncement,
    theaterNotice,
    theaterSettings,
    throwables,
    setMyUserId,
    joinTheater,
    leaveTheater,
    sendChat,
    sendThrow,
    sendReaction,
  };
}
